import { CardContent, FormHelperText, TextField, Typography } from "@mui/material";
import { MprcCard } from "components";
import { MprcContainer } from "components/MprcContainer";
import { MprcTextFieldMultiLanguage } from "components/MprcMultiLanguage";
import { MprcStringToHtml } from "components/MprcStringToHtml";
import { MprcSwitch } from "components/MprcSwitch";
import { ManagedUnitTypeResponse, ResourceModel } from "generated/CoreApi_v1";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

////////////////////////////////////////////////////////////////////////////

export interface IValidationFields {
    nameR: boolean;
    branchNameR: boolean;
    externalCode: boolean;
}

interface IProps {
    viewData: ManagedUnitTypeResponse;
    viewErrors: ApiProblemErrors | undefined;

    onRefreshForm: (disableSave: boolean) => void;
    onFormUpdate: (dto: ManagedUnitTypeResponse) => void;
    onFormValidation: (valid: boolean) => void;
}

////////////////////////////////////////////////////////////////////////////
/// FUNCTIONS
////////////////////////////////////////////////////////////////////////////

function getDefaultValidate(dto: ManagedUnitTypeResponse, defaultLocale: string): IValidationFields {
    return {
        nameR: dto.nameR != undefined && dto.nameR.length > 0 && (dto.nameR.find(p => p.locale == defaultLocale)?.text ?? "")?.length > 0,
        branchNameR: dto.branchNameR != undefined && dto.branchNameR.length > 0 && (dto.branchNameR.find(p => p.locale == defaultLocale)?.text ?? "")?.length > 0,
        externalCode: dto.externalCode != undefined && dto.externalCode.length > 0
    };
}

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Tipos de Unidades Administrativas
 * @param props
 */
export const EditUnitTypeMainBox: React.FC<IProps> = (props) => {

    const { viewData, viewErrors, onFormUpdate, onFormValidation } = props;

    const session = useSession();
    const { t } = useTranslation();

    const defaultLocaleRef = useRef<string>(session.config.defaultLocale);
    const formValidation = useRef<IValidationFields>(getDefaultValidate(viewData, defaultLocaleRef.current));

    const vErrors = viewErrors ?? {};
    const postValidation = {
        nameR: vErrors["nameR"],
        branchNameR: vErrors["branchNameR"],
        externalCode: vErrors["externalCode"],
        defineJobsAndPositions: vErrors["defineJobsAndPositions"]
    };


    ////////////////////////////////////////////////////////////////////////////
    /// Handles
    ////////////////////////////////////////////////////////////////////////////

    ///
    function handleUpdateRecord(name: keyof ManagedUnitTypeResponse, v: any) {

        onFormUpdate({ ...viewData, [name]: v });

        if (formValidation.current && formValidation.current.hasOwnProperty(name)) {
            if ((viewData[name] as ResourceModel[])?.length > 0) {
                formValidation.current = { ...formValidation.current, [name]: (viewData[name] as ResourceModel[])[0].text != "" };
            }
            else {
                formValidation.current = { ...formValidation.current, [name]: v != "" };
            }
        }
    }

    ////////////////////////////////////////////////////////////////////////////
    /// Effect's
    ////////////////////////////////////////////////////////////////////////////

    // Ao editar o DTO atual:
    useEffect(() => {
        formValidation.current = getDefaultValidate(viewData, session.config.defaultLocale);
        onFormValidation(Object.values(formValidation.current).filter(x => x === false).length == 0);
    }, [viewData]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <div>
            <MprcContainer>
                <MprcCard>
                    <CardContent>
                        <MprcTextFieldMultiLanguage
                            autoFocus
                            fullWidth
                            required
                            name="nameR"
                            maxLength={255}
                            label={t("Settings.UnitTypes.Edit.NameLabel")}
                            margin="dense"
                            currentValues={viewData.nameR}
                            locales={session.config.inputLocales}
                            onLocaleInputChange={(v) => v && handleUpdateRecord("nameR", v)}
                            error={postValidation?.nameR != undefined}
                            helperText={postValidation?.nameR && <MprcStringToHtml text={postValidation?.nameR} color="error" />}
                        />

                        <MprcTextFieldMultiLanguage
                            fullWidth
                            required
                            name="branchNameR"
                            maxLength={255}
                            label={t("Settings.UnitTypes.Edit.BranchNameRLabel")}
                            margin="dense"
                            currentValues={viewData.branchNameR}
                            locales={session.config.inputLocales}
                            onLocaleInputChange={(v) => v && handleUpdateRecord("branchNameR", v)}
                            error={postValidation?.branchNameR != undefined}
                            helperText={postValidation?.branchNameR && <MprcStringToHtml text={postValidation?.branchNameR} color="error" />}
                        />

                        <TextField
                            required
                            name="externalCode"
                            margin="dense"
                            value={viewData.externalCode || ""}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 50 }
                                }
                            }}
                            label={t("Settings.UnitTypes.Edit.ExternalCodeLabel")}
                            sx={{ width: "400px" }}
                            error={postValidation?.externalCode != undefined}
                            helperText={postValidation?.externalCode && <MprcStringToHtml text={postValidation?.externalCode} color="error" />}
                            onChange={(e) => handleUpdateRecord("externalCode", e.target.value)}
                        />

                        <MprcSwitch
                            label={t("Settings.UnitTypes.Edit.IsRequiredLabel")}
                            checked={viewData.isRequired!}
                            disabled={viewData.settings.canChangeIsRequired == false}
                            onChange={
                                () => { handleUpdateRecord("isRequired", !viewData.isRequired); }
                            }
                            color="primary"
                        />

                        <MprcSwitch
                            label={t("Settings.UnitTypes.Edit.AllowMultiSelectionLabel")}
                            checked={viewData.allowMultiSelection!}
                            onChange={
                                () => { handleUpdateRecord("allowMultiSelection", !viewData.allowMultiSelection); }
                            }
                            color="primary"
                        />

                        <MprcSwitch
                            label={t("Settings.UnitTypes.Edit.ShowOnUserProfileLabel")}
                            checked={viewData.showOnUserProfile!}
                            onChange={
                                () => { handleUpdateRecord("showOnUserProfile", !viewData.showOnUserProfile); }
                            }
                            color="primary"
                        />

                        <MprcSwitch
                            label={t("Settings.UnitTypes.Edit.DefineJobsAndPositionsLabel")}
                            checked={viewData.defineJobsAndPositions!}
                            onChange={
                                () => { handleUpdateRecord("defineJobsAndPositions", !viewData.defineJobsAndPositions); }
                            }
                            color="primary"

                        />
                        <Typography component="p" variant="caption" ml={6}>
                            {t("Settings.UnitTypes.Edit.DefineJobsAndPositionsMessage")}
                        </Typography>
                        <FormHelperText error={postValidation?.defineJobsAndPositions != undefined}>
                            {postValidation?.defineJobsAndPositions && <MprcStringToHtml text={postValidation?.defineJobsAndPositions} color="error" />}
                        </FormHelperText>
                    </CardContent>
                </MprcCard>
            </MprcContainer>
        </div>
    );
};
